import type { orchestrationFr } from './orchestration.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for orchestration (2026-10-10). Same keys as `orchestration.fr.ts`. */
export const orchestrationEn: DeepStrings<typeof orchestrationFr> = {
  schedule: {
    infrastructure: 'Infrastructure for scheduled runs',
    infrastructureHelp:
      'A scheduled run starts on its own: it needs the infrastructure a click on “Run” would have asked for.',
    timezone: 'Time zone',
    timezoneHelp: 'IANA time zone of the cron expression (Europe/Paris by default).',
    catchUp: 'Missed occurrences',
    catchUpHelp: 'What the planner does with occurrences missed while it was down (restart, leader change).',
    catchUpPolicies: {
      none: 'Skip them',
      last: 'Run the most recent one (default)',
      all: 'Run each of them',
    },
    catchUpShort: {
      none: 'catch-up: none',
      last: 'catch-up: last',
      all: 'catch-up: all',
    },
    noInfrastructure: 'No infrastructure',
  },
  retries: {
    retries: 'Retries after a failure',
    retriesHelp: '0 to 10 new attempts; each one is a new run of the job (GRAAL_ATTEMPT).',
    delay: 'Delay before a retry (s)',
    delayHelp: '0 to 86,400 seconds, 10 by default.',
    column: 'Retries',
    summary_one: '{{count}} retry · {{delay}} s',
    summary_other: '{{count}} retries · {{delay}} s',
    none: 'none',
    invalidRetries: 'Between 0 and 10.',
    invalidDelay: 'Between 0 and 86,400 seconds.',
    taskRetries: 'Retries — {{task}}',
    taskDelay: 'Delay (s) — {{task}}',
  },
  attempts: {
    title: 'Step attempts',
    description:
      'One row per attempt: a step retried after a failure has several, each with its own job run.',
    step: 'Step',
    attempt: 'Attempt',
    status: 'Status',
    jobRun: 'Job run',
    error: 'Error',
    started: 'Started',
    openJobRun: 'Open the run',
    empty: 'No attempt yet',
    emptyHelp: 'Attempts appear as soon as the engine launches the first step.',
    statuses: {
      starting: 'Starting',
      running: 'Running',
      succeeded: 'Succeeded',
      failed: 'Failed',
    },
  },
  backfills: {
    tab: 'Backfills',
    title: 'Backfills',
    description:
      'Replay the cron schedule over a past interval: one run per occurrence, each with its logical date (GRAAL_LOGICAL_DATE).',
    notCron: 'No backfill without a cron schedule',
    notCronHelp:
      'A backfill replays the occurrences of a cron expression. Schedule this resource first (Definition or Summary tab).',
    create: 'New backfill',
    createTitle: 'Replay a past interval',
    createDescription:
      'One run per occurrence of “{{cron}}” between start and end, at most N at a time, like a manual run (quotas included).',
    start: 'Start',
    end: 'End',
    dateHelp: 'ISO 8601 date, for instance 2026-10-01 or 2026-10-01T06:00:00Z.',
    maxParallel: 'Simultaneous runs',
    maxParallelHelp: 'From 1 to 5.',
    infrastructure: 'Infrastructure',
    infrastructureHelp: 'By default, the one of the cron schedule.',
    defaultInfrastructure: 'The schedule’s one',
    submit: 'Start the backfill',
    created: 'Backfill started: {{total}} occurrences.',
    createFailed: 'The backfill could not be started.',
    cancel: 'Cancel',
    cancelled: 'Backfill cancelled: pending occurrences will not start.',
    cancelFailed: 'The backfill could not be cancelled.',
    invalidDate: 'Invalid date.',
    endInFuture: 'The end cannot be in the future.',
    endBeforeStart: 'The end must come after the start.',
    invalidParallel: 'Between 1 and 5.',
    columns: {
      period: 'Interval',
      status: 'Status',
      progress: 'Progress',
      parallel: 'Simultaneous',
      createdBy: 'Started by',
      created: 'Created',
    },
    progress: '{{done}} / {{total}} done',
    progressDetail:
      '{{succeeded}} succeeded · {{failed}} failed · {{launched}} running · {{pending}} pending',
    statuses: {
      running: 'Running',
      complete: 'Complete',
      cancelled: 'Cancelled',
    },
    empty: 'No backfill',
    emptyHelp: 'Replay a missed period: one run per cron occurrence.',
  },
  triggers: {
    title: 'Triggers',
    subtitle:
      'Launch a job or a workflow of {{project}} when an event arrives: the end of a run, an object dropped in a bucket, a signed call.',
    subtitle_unnamed:
      'Launch a job or a workflow of this project when an event arrives: the end of a run, an object dropped in a bucket, a signed call.',
    create: 'New trigger',
    createTitle: 'New trigger',
    createSubtitle: 'One event launches at most one run of the target.',
    created: 'Trigger {{name}} created.',
    deleted: 'Trigger {{name}} deleted.',
    deleteFailed: 'The trigger could not be deleted.',
    delete: 'Delete the trigger',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    toggled: 'Trigger {{name}}: {{state}}.',
    toggleFailed: 'The trigger could not be updated.',
    empty: 'No trigger',
    emptyHelp: 'Chain your processing on an event rather than at a fixed time.',
    types: {
      run_completed: 'End of a run',
      object_created: 'Object dropped',
      webhook: 'Signed webhook',
    },
    typeHelp: {
      run_completed: 'The end of a run of a job or a workflow of the project, by its status.',
      object_created:
        'An object created under a prefix of a bucket. Objects present when the trigger is created do not fire.',
      webhook: 'A signed HTTP call (HMAC-SHA256) from an external system.',
    },
    kinds: { job: 'Job', workflow: 'Workflow', bucket: 'Bucket' },
    endStatuses: { complete: 'Succeeded', failed: 'Failed', killed: 'Killed' },
    fields: {
      name: 'Name',
      description: 'Description',
      type: 'Event',
      targetKind: 'Target',
      targetJob: 'Job launched',
      targetWorkflow: 'Workflow launched',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'Where the target runs; by default, the one of its cron schedule.',
      sourceKind: 'Source',
      sourceJob: 'Job watched',
      sourceWorkflow: 'Workflow watched',
      sourceBucket: 'Bucket watched',
      prefix: 'Key prefix',
      prefixHelp: 'For instance “incoming/”. Empty: the whole bucket.',
      onComplete: 'When the run succeeds',
      onFailed: 'When the run fails',
      onKilled: 'When the run is killed',
      enabled: 'Enabled on creation',
    },
    atLeastOneStatus: 'Choose at least one end status.',
    sameAsTarget: 'The source cannot be the target itself.',
    columns: {
      name: 'Name',
      type: 'Event',
      target: 'Target',
      source: 'Source',
      state: 'State',
      lastFired: 'Last fired',
    },
    never: 'never',
    detail: {
      tabs: { summary: 'Summary', events: 'Events', webhook: 'Webhook' },
      type: 'Event',
      target: 'Target',
      source: 'Source',
      statuses: 'End statuses',
      prefix: 'Prefix',
      infrastructure: 'Infrastructure',
      owner: 'Created by',
      created: 'Created',
      lastFired: 'Last fired',
      openTarget: 'Open the target',
    },
    events: {
      title: 'Event log',
      description: 'The last events seen, most recent first. A duplicate launches nothing.',
      key: 'Key',
      status: 'Status',
      run: 'Run launched',
      detail: 'Detail',
      at: 'Received',
      openRun: 'Open the run',
      empty: 'No event received',
      emptyHelp: 'Events appear here as soon as the source produces some.',
      statuses: {
        pending: 'Pending',
        fired: 'Run launched',
        failed: 'Failed',
        duplicate: 'Duplicate',
      },
    },
    webhook: {
      title: 'Call the webhook',
      description:
        'No token: the request is authenticated by its signature. The timestamp must be within 5 minutes of the server clock; any refusal gets the same 401.',
      url: 'Webhook URL',
      signature: 'Signature',
      signatureHelp:
        'X-Graal-Signature: sha256=<hex> = HMAC-SHA256(secret, timestamp + "." + body), X-Graal-Timestamp in Unix seconds, X-Graal-Delivery (optional) deduplicates.',
      example: 'Example (bash) — the secret is read from the keyboard, never written in the command',
      rotate: 'Rotate the secret',
      rotateHint: 'The old secret stops signing at once: update the caller.',
      rotated: 'New secret issued.',
      rotateFailed: 'The secret could not be rotated.',
      secret: 'Signing secret',
      copySecret: 'Copy the secret',
      once: 'Shown only once: it will never be read again. Keep it in your vault.',
      noSecret: 'The server returned no secret: rotate it.',
      notWebhook: 'This trigger is not a webhook.',
      done: 'I have saved the secret',
      revealTitle: 'Webhook {{name}} ready',
    },
  },
};
